<template>
  <KioskCenteredLayout>
    <KioskSpinner v-if="loading"/>
    <div v-else
         :class="$style.payment">
      <svg v-svg
           :class="$style.paymentIcon"
           symbol="card-icon" />
      <h2 :class="$style.totalPrice">{{ totalPrice | toPrice }}</h2>
      <p :class="$style.paymentText">Insert, or Swipe card when ready</p>
      <BaseButton
        :class="$style.cancelBtn"
        button-style="Danger"
        @click="cancel">
        Cancel
      </BaseButton>
    </div>
  </KioskCenteredLayout>
</template>

<script>
import Component from 'vue-class-component';
import Vue from 'vue';
import { Action, Getter } from 'vuex-class';
import to from 'await-to-js';
import KioskCenteredLayout from '@/layouts/KioskCenteredLayout';
import KioskMessageTitle from '@/components/KioskMessageTitle';
import { AUTH_GUEST_TOKEN, AUTH_LOGOUT } from '@/apps/auth/store/types';
import { API_ERROR_SET } from '@/apps/core/store/types';
import {
  CART_PREPARE_ORDER,
  PAYMENT_INTENT,
  CART_RESET,
  TERMINAL_CONNECTION_TOKEN,
  CAPTURE_ORDER,
} from '@/apps/cart/store/types';
import BaseButton from '@/components/BaseButton';
import KioskSpinner from './KioskSpinner';

Component.registerHooks([
  'beforeRouteLeave',
]);

/**
 * Provide payment page
 */
@Component({
  components: {
    BaseButton,
    KioskSpinner,
    KioskCenteredLayout,
    KioskMessageTitle,
  },
})
export default class KioskPayment extends Vue {
  @Getter order;
  @Getter orderTotalPrice;
  @Getter isGuest;
  @Getter terminalId;
  @Action(CART_PREPARE_ORDER) prepareOrder;
  @Action(AUTH_GUEST_TOKEN) getNewGuestToken;
  @Action(CART_RESET) cartReset;
  @Action(PAYMENT_INTENT) paymentIntent;
  @Action(CAPTURE_ORDER) captureOrder;
  @Action(TERMINAL_CONNECTION_TOKEN) connectionToken;
  @Action(API_ERROR_SET) setApiError;
  @Action(AUTH_LOGOUT) logout;

  loading = false;
  terminal = null;

  get totalPrice() {
    if (this.order) {
      const priceWithDiscount =
        Number(this.orderTotalPrice) - Number(this.order.discountPrice);

      return priceWithDiscount + Number(this.order.tax);
    }

    return 0;
  }

  mounted() {
    this.terminal = this.createTerminal();
    this.discoverReaders();
  }

  cancel() {
    this.terminal.cancelCollectPaymentMethod();
    this.logout();
    this.$router.push({ name: 'main' });
  }

  /**
   * Handle terminal connect error
   */
  unexpectedDisconnect() {
    this.setApiError({ detail: 'unexpected terminal disconnect' });
  }

  /**
   * Create stripe terminal
   */
  createTerminal() {
    /* global StripeTerminal */
    return StripeTerminal.create({
      onFetchConnectionToken:       this.connectionToken,
      onUnexpectedReaderDisconnect: this.unexpectedDisconnect,
    });
  }

  /**
   * Search and connect to terminal reader by terminal id
   */
  async connectReader(discoverResult) {
    const selectedReader = discoverResult.discoveredReaders
      .find(reader => reader.id === this.terminalId);

    if (selectedReader && selectedReader.status === 'offline') {
      this.setApiError({ detail: 'Reader is offline.' });

      throw new Error('Reader is offline.');
    }

    if (!selectedReader) {
      this.setApiError({ detail: 'No available readers.' });

      throw new Error('No available readers');
    }

    try {
      const connectResult = await this.terminal.connectReader(selectedReader);

      if (connectResult.error) {
        this.setApiError({ detail: connectResult.error.message });
      }
    } catch (e) {
      this.setApiError({ detail: 'Fated reader connect.', originError: e });
      throw new Error(e);
    }
  }

  /**
   * Discover readers for terminal
   */
  async discoverReaders() {
    const config = { simulated: false };

    this.loading = true;

    try {
      const discoverResult = await this.terminal.discoverReaders(config);

      if (discoverResult.error) {
        this.setApiError({ detail: `Failed to discover: ${discoverResult.error}` });

        return;
      }

      if (discoverResult.discoveredReaders.length === 0) {
        this.setApiError({ detail: 'No available readers.' });

        return;
      }

      await this.connectReader(discoverResult);
      await this.checkout();
    } catch (e) {
      this.setApiError({ detail: e.message, originError: e });
    } finally {
      this.loading = false;
    }
  }


  /**
   * Get payment intent for order
   */
  async getPaymentIntent() {
    const [ , res ] = await to(this.paymentIntent({}));

    if (res && res.error) {
      this.setApiError({ detail: res.error });
    }

    return res;
  }

  /**
   * Capture payment intent
   * @param token - obj with payment intent id
   */
  async capturePaymentIntent(token) {
    const [ , res ] = await to(this.captureOrder({
      intentId: token.paymentIntentId,
    }));


    if (res && res.error) {
      this.setApiError({ detail: res.error });

      return;
    }

    // TODO: need add getting Aloha Order Number.
    if (res.success) {
      this.cartReset();
      this.$router.push({ name: 'confirmation' });
    }
  }


  /**
   * Send information about order to API and show animation
   */
  async checkout() {
    this.loading = true;

    try {
      const { paymentIntentClientSecret } = await this.getPaymentIntent();

      this.loading = false;

      // Wait payment.
      const paymentIntentResult =
        await this.terminal.collectPaymentMethod(paymentIntentClientSecret);

      if (paymentIntentResult.error) {
        this.setApiError({ detail: paymentIntentResult.error.message });

        return;
      }

      this.loading = true;

      const processPaymentResult =
        await this.terminal.processPayment(paymentIntentResult.paymentIntent);

      if (processPaymentResult.error) {
        this.setApiError({ detail: processPaymentResult.error.message });

        return;
      }

      if (!processPaymentResult.paymentIntent || processPaymentResult.paymentIntent.status !== 'requires_capture') {
        this.setApiError({ detail: 'process payment error' });

        return;
      }

      await this.capturePaymentIntent({
        paymentIntentId: processPaymentResult.paymentIntent.id,
      });
    } catch (e) {
      this.setApiError({ detail: e.message, originError: e });
    } finally {
      this.loading = false;
    }
  }

  async beforeRouteLeave(to, from, next) {
    if (this.isGuest) {
      await this.getNewGuestToken();
    }
    this.loading = false;
    next();
  }
}
</script>

<style lang="scss" module>
@import '~@/style/variables.scss';

.payment {
  text-align: center;
}

.paymentIcon {
  width: 237px;
  height: 216px;
}

.totalPrice {
  margin-top: 220px;
  margin-bottom: 34px;
  color: $white;
  font-size: 140px;
  line-height: 133px;
}

.paymentText {
  color: $white;
  font-size: 34px;
  line-height: 48px;
}

.cancelBtn {
  margin-top: 50px;
  min-width: 300px;
  height: 100px;
  border-radius: 50px !important;

  div {
    font-size: 30px !important;
    line-height: 38px !important;
  }
}
</style>



// WEBPACK FOOTER //
// src/apps/kiosk/components/KioskPayment.vue